import React from 'react'
import { ReviewEditFieldModel } from '../../../../api/types'
import {
  FieldPreviewAdorned,
  FieldPreviewAdornedInput,
  FieldPreviewAdornment,
  FieldPreviewControl,
  FieldPreviewGrid,
  FieldPreviewHelp,
  FieldPreviewLabel,
  FieldPreviewRequired,
  FieldPreviewTextArea,
  PreviewOptionRow,
  PreviewRadioList,
  PreviewRadioMark,
  PreviewSelectBox,
} from '../../style'
import {
  FactoryAddNote,
  FactoryGroupBlock,
  FactoryGroupDescription,
  FactoryGroupTitle,
  FactoryUploadBox,
  FieldPreviewItem,
  PreviewCheckMark,
  PreviewOptionList,
  PreviewSelectValue,
} from './style'

/** Collapse a factory field's display-name type (e.g. "text-area") to a comparable token ("textarea"). */
const normalize = (type: string | null): string => (type ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')

const parseWidth = (width: string | null | undefined): number => {
  const parsed = parseInt(width ?? '', 10)
  return Number.isFinite(parsed) && parsed > 0 && parsed <= 100 ? parsed : 100
}

const flexBasis = (width: string | null | undefined): string => `calc(${parseWidth(width)}% - 0.5rem)`

const asString = (value: unknown): string => {
  return typeof value === 'string' ? value : ''
}

/**
 * Renders one field from the backend agent edit-view model (`ReviewEditFieldModel`) as a read-only preview,
 * recursing into groups/collections/options. Purely presentational — mirrors what the agent app renders for a
 * component-factory section so the builder shows a like-for-like preview.
 */
export const FactoryFieldPreview: React.FC<{ field: ReviewEditFieldModel }> = ({ field }) => {
  const type = normalize(field.type)
  const config = field.config
  const label = field.title || ''
  const required = Boolean(config?.isRequired)
  const placeholder = config?.placeholder ?? ''
  const value = asString(field.value)
  const children = field.fields ?? []

  // Composite containers span the full row and hold a nested grid.
  if (type === 'group' || type === 'collection') {
    return (
      <FactoryGroupBlock>
        {(field.title || field.description) && (
          <div>
            {field.title && <FactoryGroupTitle>{field.title}</FactoryGroupTitle>}
            {field.description && <FactoryGroupDescription>{field.description}</FactoryGroupDescription>}
          </div>
        )}
        <FieldPreviewGrid>
          {children.map((child, index) => (
            <FactoryFieldPreview key={child.id ?? index} field={child} />
          ))}
        </FieldPreviewGrid>
        {type === 'collection' && config?.allowAddSections && (
          <FactoryAddNote>+ {config.addButtonText || 'Add'}</FactoryAddNote>
        )}
      </FactoryGroupBlock>
    )
  }

  if (type === 'checkboxgroup') {
    return (
      <FieldPreviewItem style={{ flex: '0 0 100%' }}>
        <PreviewOptionList>
          {children.map((child, index) => (
            <PreviewOptionRow key={child.id ?? index}>
              <PreviewCheckMark />
              {child.title}
            </PreviewOptionRow>
          ))}
        </PreviewOptionList>
      </FieldPreviewItem>
    )
  }

  if (type === 'radiogroup') {
    return (
      <FieldPreviewItem style={{ flex: `0 0 ${flexBasis(config?.width)}` }}>
        {label && (
          <FieldPreviewLabel>
            {label}
            {required && <FieldPreviewRequired>*</FieldPreviewRequired>}
          </FieldPreviewLabel>
        )}
        <PreviewRadioList>
          {children.map((child, index) => (
            <PreviewOptionRow key={child.id ?? index}>
              <PreviewRadioMark />
              {child.title}
            </PreviewOptionRow>
          ))}
        </PreviewRadioList>
      </FieldPreviewItem>
    )
  }

  const renderControl = () => {
    switch (type) {
      case 'textarea':
        return <FieldPreviewTextArea rows={3} readOnly disabled placeholder={placeholder} value={value} />
      case 'select': {
        const selected = (config?.options ?? []).find((option) => asString(option.value) === value)
        return (
          <PreviewSelectBox>
            {selected ? <PreviewSelectValue>{selected.title}</PreviewSelectValue> : placeholder || 'Select…'}
            <span aria-hidden>▾</span>
          </PreviewSelectBox>
        )
      }
      case 'currency':
        return (
          <FieldPreviewAdorned>
            <FieldPreviewAdornment>$</FieldPreviewAdornment>
            <FieldPreviewAdornedInput readOnly disabled inputMode="decimal" placeholder={placeholder} value={value} />
          </FieldPreviewAdorned>
        )
      case 'attachment':
        return <FactoryUploadBox>Upload files</FactoryUploadBox>
      case 'date':
        return <FieldPreviewControl readOnly disabled placeholder={placeholder || 'dd/mm/yyyy'} value={value} />
      default:
        return <FieldPreviewControl readOnly disabled placeholder={placeholder} value={value} />
    }
  }

  return (
    <FieldPreviewItem style={{ flex: `0 0 ${flexBasis(config?.width)}` }}>
      {label && (
        <FieldPreviewLabel>
          {label}
          {required && <FieldPreviewRequired>*</FieldPreviewRequired>}
        </FieldPreviewLabel>
      )}
      {renderControl()}
      {field.description && <FieldPreviewHelp>{field.description}</FieldPreviewHelp>}
    </FieldPreviewItem>
  )
}

/** Renders a full typed section's factory-built fields as a like-for-like agent preview. */
export const FactorySectionPreview: React.FC<{ fields: ReviewEditFieldModel[] }> = ({ fields }) => (
  <FieldPreviewGrid>
    {fields.map((field, index) => (
      <FactoryFieldPreview key={field.id ?? index} field={field} />
    ))}
  </FieldPreviewGrid>
)
